/**
 * Background color definitions with light and dark mode variants
 * 
 * Instead of storing hex colors directly in the database, we store color names
 * and resolve them to the appropriate hex value based on the current theme mode.
 */

export const BACKGROUND_COLORS = {
  default: {
    name: "Default",
    light: "#eef1ee", // App background (matches the ink/brass theme's light paper background)
    dark: "#1a223f", // Dark blue-gray (matches theme default)
  },
  white: {
    name: "Dark Blue",
    light: "#ffffff",
    dark: "#1a223f", // Dark blue-gray (matches theme default)
  },
  lightGray: {
    name: "Light Gray",
    light: "#f5f5f5",
    dark: "#2d2d2d", // Dark gray
  },
  blue: {
    name: "Blue",
    light: "#e3f2fd", // Light blue
    dark: "#0d47a1", // Dark blue
  },
  green: {
    name: "Green",
    light: "#e8f5e9", // Light green
    dark: "#1b5e20", // Dark green
  },
  purple: {
    name: "Purple",
    light: "#f3e5f5", // Light purple
    dark: "#4a148c", // Dark purple
  },
  navy: {
    name: "Navy",
    light: "#e8eaf6", // Very light indigo
    dark: "#1a237e", // Dark navy
  },
  teal: {
    name: "Teal",
    light: "#e0f2f1", // Light teal
    dark: "#004d40", // Dark teal
  },
  rose: {
    name: "Rose",
    light: "#fce4ec", // Light pink
    dark: "#880e4f", // Dark rose
  },
  amber: {
    name: "Amber",
    light: "#fff8e1", // Light amber/yellow
    dark: "#e65100", // Dark amber/orange
  },
};

/**
 * Get the hex color value for a color name based on theme mode
 * @param {string} colorName - The color name (e.g., "white", "blue")
 * @param {string} mode - Theme mode: "light" or "dark"
 * @returns {string|null} The hex color value or null if color name not found
 */
export const getBackgroundColor = (colorName, mode = "light") => {
  const color = BACKGROUND_COLORS[colorName];
  if (!color) {
    return null;
  }
  return mode === "dark" ? color.dark : color.light;
};

/**
 * Check if a value is a valid color name
 * @param {string} value - The value to check
 * @returns {boolean} True if the value is a valid color name
 */
export const isValidColorName = (value) => {
  return value && BACKGROUND_COLORS.hasOwnProperty(value);
};

/**
 * Check if a value is a hex color (for backward compatibility)
 * @param {string} value - The value to check
 * @returns {boolean} True if the value is a hex color
 */
export const isHexColor = (value) => {
  return value && typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value);
};

/**
 * Get all available background colors as an array
 * @returns {Array} Array of color objects with name, key, light, and dark properties
 */
export const getBackgroundColorsList = () => {
  return Object.entries(BACKGROUND_COLORS).map(([key, color]) => ({
    key,
    name: color.name,
    light: color.light,
    dark: color.dark,
  }));
};

